import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import AuthService from '../services/AuthService';
import {
  BORDER_RADIUS,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
} from '../constants/layout';
import { FONT_TITLE3, FONT_BODY, FONT_FOOTNOTE } from '../constants/typography';

export default function ChangeEmailModal({ isOpen, onClose, currentEmail, onSaved }) {
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewEmail('');
      setPassword('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = () => {
    const trimmed = newEmail.trim();
    return trimmed.length > 0
      && trimmed.includes('@')
      && trimmed.toLowerCase() !== (currentEmail || '').toLowerCase()
      && password.length > 0;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await AuthService.reauthenticate(password);
      const functions = getFunctions(getApp(), 'us-central1');
      const updateUserEmail = httpsCallable(functions, 'updateUserEmail');
      await updateUserEmail({ email: newEmail.trim() });
      setSuccess(true);
      onSaved(newEmail.trim().toLowerCase());
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password.');
      } else {
        setError(err.message || 'Failed to update email.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cem-overlay" onClick={() => !saving && onClose()}>
      <div className="cem-content" onClick={e => e.stopPropagation()}>
        <div className="cem-header">
          <h3>Change Email</h3>
          <button className="cem-close" onClick={() => !saving && onClose()} disabled={saving}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="cem-body">
          {success ? (
            <div className="cem-success">
              <span className="material-icons cem-success-icon">check_circle</span>
              <p>Your email has been changed to {newEmail.trim().toLowerCase()}.</p>
            </div>
          ) : (
            <>
              <div className="cem-field">
                <label className="cem-label">Current Email</label>
                <div className="cem-static">{currentEmail}</div>
              </div>
              <div className="cem-field">
                <label className="cem-label">New Email</label>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  className="cem-input" placeholder="New email address" autoComplete="email" disabled={saving} autoFocus />
              </div>
              <div className="cem-field">
                <label className="cem-label">Confirm Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="cem-input" placeholder="Password" autoComplete="current-password" disabled={saving} />
              </div>
              {error && <p className="cem-error">{error}</p>}
            </>
          )}
        </div>
        <div className="cem-footer">
          {success ? (
            <button onClick={onClose} className="cem-save">OK</button>
          ) : (
            <>
              <button onClick={onClose} className="cem-cancel" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="cem-save" disabled={saving || !isValid()}>
                {saving ? 'Updating...' : 'Update Email'}
              </button>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .cem-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 1100; padding: ${SPACING_CARD};
        }
        .cem-content {
          background-color: var(--color-background, #fff);
          border-radius: ${BORDER_RADIUS};
          max-width: 420px; width: 100%;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
        }
        .cem-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          border-bottom: 1px solid var(--color-border);
        }
        .cem-header h3 {
          font-size: ${FONT_TITLE3}; font-weight: 600; margin: 0;
          color: var(--color-text-primary);
        }
        .cem-close {
          background: none; border: none; cursor: pointer;
          padding: 4px; color: var(--color-text-secondary);
        }
        .cem-close:hover { color: var(--color-text-primary); }
        .cem-body { padding: ${SPACING_CARD_BODY}; }
        .cem-field { margin-bottom: ${SPACING_CARD}; }
        .cem-field:last-child { margin-bottom: 0; }
        .cem-label {
          display: block; font-size: ${FONT_FOOTNOTE}; font-weight: 600;
          color: var(--color-text-primary); margin-bottom: ${SPACING_ELEMENT};
        }
        .cem-input {
          width: 100%; padding: ${SPACING_COMPONENT};
          border: 1px solid var(--color-border); border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY}; color: var(--color-text-primary);
          background-color: var(--color-surface, #fff); box-sizing: border-box;
        }
        .cem-input:focus { outline: none; border-color: var(--color-accent); }
        .cem-input:disabled { opacity: 0.6; cursor: not-allowed; }
        .cem-static {
          font-size: ${FONT_BODY}; color: var(--color-text-secondary);
          padding: ${SPACING_COMPACT} 0 0;
        }
        .cem-error {
          color: #ef4444; font-size: ${FONT_FOOTNOTE}; margin: ${SPACING_BUTTON} 0 0;
        }
        .cem-footer {
          display: flex; justify-content: flex-end; gap: ${SPACING_COMPONENT};
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          border-top: 1px solid var(--color-border);
        }
        .cem-cancel, .cem-save {
          padding: ${SPACING_COMPACT} ${SPACING_CARD}; border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY}; font-weight: 600; cursor: pointer;
        }
        .cem-cancel {
          background-color: transparent; color: var(--color-text-secondary);
          border: 1px solid var(--color-border);
        }
        .cem-cancel:hover:not(:disabled) { background-color: var(--color-surface-muted); }
        .cem-save { background-color: var(--color-accent); color: var(--color-accent-text); }
        .cem-save:hover:not(:disabled) { background-color: var(--color-accent-hover); }
        .cem-save:disabled, .cem-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
        .cem-success {
          display: flex; flex-direction: column; align-items: center;
          gap: ${SPACING_ELEMENT}; padding: ${SPACING_CARD_BODY} 0;
        }
        .cem-success-icon { font-size: 48px; color: #22c55e; }
        .cem-success p {
          margin: 0; color: var(--color-text-primary);
          font-weight: 600; text-align: center;
        }
      `}</style>
    </div>
  );
}
